import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadIntake, clearIntake } from "../lib/intakeStore";
import { api } from "../lib/api";
import TopNav from "../components/TopNav";
import { trackEvent } from "../utils/analytics";
import { useLang } from "../lib/i18n";

export default function IntakeFinalize() {
  const nav = useNavigate();
  const [err, setErr] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [processing, setProcessing] = useState(false);
  const { lang } = useLang();

  const submit = async () => {
    setProcessing(true); setErr("");
    const intake = loadIntake();
    if (!intake.answers || Object.keys(intake.answers).length === 0) {
      nav("/intake/start?empty=1"); return;
    }
    try {
      const r = await api.post("/intake/submit", { answers: intake.answers });
      // Persist the anonymous session token so /intake/results can find the submission.
      if (r?.data?.session_token) {
        localStorage.setItem("aabb_intake_session", r.data.session_token);
      }
      trackEvent("form_submit", { form: "intake" });
      clearIntake();
      setSubmitted(true);
      setProcessing(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || (lang === "es" ? "El envío falló. Por favor intenta de nuevo." : "Submission failed. Please retry."));
      setProcessing(false);
    }
  };

  useEffect(() => { submit(); /* eslint-disable-next-line */ }, []);

  const handleContinue = () => {
    nav("/intake/results");
  };

  return (
    <>
      <TopNav minimal />
      <div className="aa-shell aa-center" style={{paddingTop: "100px"}}>
        {processing && !err && <>
          <div className="aa-eyebrow">{lang === "es" ? "Revisando tus respuestas" : "Reviewing your answers"}</div>
          <h1 className="aa-h1">{lang === "es" ? "Casi listo…" : "Almost done…"}</h1>
          <p className="aa-sub" style={{marginInline:"auto"}}>{lang === "es" ? "Nuestro equipo médico está preparando tus recomendaciones personalizadas." : "Our medical team is preparing your personalized recommendations."}</p>

          {/* Loading Animation */}
          <div style={{marginTop: 40, display: "flex", flexDirection: "column", alignItems: "center", gap: 20}}>
            <div data-testid="intake-finalize-spinner" style={{
              width: 60,
              height: 60,
              borderRadius: "50%",
              border: "5px solid rgba(193,127,58,.2)",
              borderTopColor: "var(--gold)",
              animation: "aa-spin 1s linear infinite",
            }}/>
            <div style={{
              display: "flex",
              gap: 8,
              alignItems: "center"
            }}>
              <div style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: "var(--gold)",
                animation: "aa-pulse 1.5s ease-in-out infinite",
              }}/>
              <div style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: "var(--gold)",
                animation: "aa-pulse 1.5s ease-in-out 0.3s infinite",
              }}/>
              <div style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: "var(--gold)",
                animation: "aa-pulse 1.5s ease-in-out 0.6s infinite",
              }}/>
            </div>
            <p style={{color: "var(--muted)", fontSize: ".9rem", marginTop: 10}}>
              {lang === "es" ? "Procesando tu información..." : "Processing your information..."}
            </p>
          </div>

          <style>{`
            @keyframes aa-spin {
              to { transform: rotate(360deg); }
            }
            @keyframes aa-pulse {
              0%, 100% { opacity: 0.3; transform: scale(0.8); }
              50% { opacity: 1; transform: scale(1.2); }
            }
          `}</style>
        </>}

        {submitted && !err && <>
          <div className="aa-eyebrow">{lang === "es" ? "¡Éxito!" : "Success!"}</div>
          <h1 className="aa-h1">{lang === "es" ? "Tus respuestas han sido enviadas" : "Your answers have been submitted"}</h1>
          <p className="aa-sub" style={{marginInline:"auto"}}>{lang === "es" ? "Nuestro equipo médico ha recibido tu información y está preparando tus recomendaciones personalizadas." : "Our medical team has received your information and is preparing your personalized recommendations."}</p>

          {/* Continue Button */}
          <div style={{marginTop: 40}}>
            <button className="aa-btn" onClick={handleContinue} data-testid="continue-to-results">
              {lang === "es" ? "Ver Mis Recomendaciones →" : "View My Recommendations →"}
            </button>
          </div>
        </>}

        {err && <>
          <div className="aa-eyebrow">Something went wrong</div>
          <h1 className="aa-h1">Let's try again</h1>
          <p className="aa-sub" style={{marginInline:"auto"}}>{err}</p>
          <button className="aa-btn" onClick={submit} data-testid="finalize-retry">Retry</button>
        </>}
      </div>
    </>
  );
}
